import { userIsAdmin } from '@redviking/argonaut-core-ui/src/auth';
import { type ValidationResult } from 'types';
import { GetGetLocalAuthSettingsUsingContextIdsDocument, GetLocalAuthSettingsForAUserDocument } from 'types/db';
import type { UserEntity } from './user.entity';
import { gqlClient } from 'src/util/gql-client';
import type { AuthProviderLocalSettingConfig } from '@redviking/argonaut-util/src/api/auth-provider/local';

export function isAdmin (): boolean {
    return userIsAdmin();
}

export const aggregatePasswordRequirements = (configs: AuthProviderLocalSettingConfig['passwordPolicy'][]) => {
    let maxLengh = 0;
    let numbers = false;
    let lowercase = false;
    let uppercase = false;
    let specialChars = false;
    for (const config of configs) {
        if (config.lowercase) {
            lowercase = true;
        }
        if (config.specialChars) {
            specialChars = true;
        }
        if (config.numbers) {
            numbers = true;
        }
        if (config.uppercase) {
            uppercase = true;
        }
        if (config.minLength > maxLengh) {
            maxLengh = config.minLength;
        }
    }
    return {
        numbers,
        maxLengh,
        lowercase,
        uppercase,
        specialChars,
    };
};

export const validateOldPasswordRequirementsAreStillStrongerThanNewPassworRequirements = (payload: {
    newRequirements: {
        numbers: boolean;
        maxLengh: number;
        lowercase: boolean;
        uppercase: boolean;
        specialChars: boolean;
    };
    oldRequirements: {
        numbers: boolean;
        maxLengh: number;
        lowercase: boolean;
        uppercase: boolean;
        specialChars: boolean;
    };
}): boolean => {
    if (payload.newRequirements.lowercase && !payload.oldRequirements.lowercase) {
        return false;
    }
    if (payload.newRequirements.numbers && !payload.oldRequirements.numbers) {
        return false;
    }
    if (payload.newRequirements.specialChars && !payload.oldRequirements.specialChars) {
        return false;
    }
    if (payload.newRequirements.uppercase && !payload.oldRequirements.uppercase) {
        return false;
    }
    if (payload.newRequirements.maxLengh > payload.oldRequirements.maxLengh) {
        return false;
    }
    return true;
};

export const userValidations = async (user: UserEntity, originalEntity: UserEntity | null): Promise<ValidationResult> => {
    if (!user.username || user.username.length <= 0) {
        return {
            status: 'error',
            errorMessage: 'Username required',
        };
    }

    if (originalEntity) {
        const {
            argo_user_by_pk,
        } = await gqlClient.request({
            document: GetLocalAuthSettingsForAUserDocument,
            variables: {
                userId: user.id,
            },
        });
        if (!argo_user_by_pk) {
            return {
                status: 'error',
                errorMessage: 'Could not look up existing user data',
            };
        }
        const originalPolicies = argo_user_by_pk.user_contexts.filter(i => i.context.auth_provider_local_setting?.enabled).map(i => i.context.auth_provider_local_setting!.config.passwordPolicy);

        const newContextIds = user.userContexts.filter(c => !originalEntity.userContexts.find(oC => oC.context_id === c.context_id)).map(i => i.context_id);
        const {
            argo_context,
        } = await gqlClient.request({
            document: GetGetLocalAuthSettingsUsingContextIdsDocument,
            variables: {
                contextIds: newContextIds,
            },
        });
        const newPolicies = argo_context.filter(i => i.auth_provider_local_setting?.enabled).map(i => i.auth_provider_local_setting!.config.passwordPolicy);

        if (!validateOldPasswordRequirementsAreStillStrongerThanNewPassworRequirements({
            newRequirements: aggregatePasswordRequirements(newPolicies),
            oldRequirements: aggregatePasswordRequirements(originalPolicies),
        })) {
            return {
                status: 'error',
                errorMessage: 'New contexts have stricter password requirements that current contexts, the user password must be changed',
            };
        }
    }

    return {
        status: 'success',
    };
};
